import { ref, computed } from 'vue'
import http from '../../http-common'

/**
 * The clinical protocol this account's doses are validated against.
 *
 * ── Why the client fetches ceilings instead of holding its own copy ─────────
 * The dose limits used to be a literal in useDoseValidation.js and a separate
 * granularity constant in useDoseCalculations.js, each with a comment promising
 * they matched the server. They are one thing, and the server is the one that
 * rejects a write, so the client should never be the place a limit is decided.
 * Everything here comes from GET /clinical/profile.
 *
 * ── The client's copy is a courtesy, not a control ──────────────────────────
 * Validating in the browser exists so a pharmacist finds out about a ceiling
 * while typing rather than after saving. It is not a security or safety
 * boundary: the server checks every write against its own resolved protocol,
 * and will reject one the client let through. Never treat these values as
 * authoritative — treat them as a preview of the server's answer.
 *
 * ── Adjusted limits ────────────────────────────────────────────────────────
 * An account may override individual limits of the protocol it chose, in either
 * direction. `safety` below is already the adjusted set — the server merges it
 * before the profile is serialised, so nothing that validates a dose has to
 * know the layer exists. What the SETTINGS screen needs on top of that is
 * `overrides` (what was changed and why), `baseSafety` (what the protocol
 * itself says, to show beside it) and `overridable` (which fields may be
 * adjusted at all, generated server-side from the same table that applies
 * them, so this screen can never offer a field the server would reject).
 *
 * Singleton state: the protocol is account-wide, so every component that asks
 * shares one fetch.
 */

const catalogue = ref(null)
const profile = ref(null)
const loading = ref(false)
const error = ref(null)

/**
 * Per-field errors from the last rejected override save, keyed by field.
 *
 * Kept separate from `error`, which is the one-line banner. A limits form needs
 * the message attached to the input it belongs to; a single string at the top
 * of the screen makes a fixable mistake look like a failed save.
 */
const fieldErrors = ref({})

/**
 * HTTP status of the last failed LOAD, or null.
 *
 * A failure to load is not one thing: 401 means the session is not (or no
 * longer) established, and the screen should say so and offer to retry rather
 * than reporting an axios string; anything else is a server or network problem
 * the pharmacist can do nothing about but retry. Kept separate from `error`,
 * which carries the server's own wording where it has any.
 */
const errorStatus = ref(null)

/** Non-blocking notes from the last override save — a value the server accepted but wants confirmed. */
const advisories = ref([])

/** In-flight fetch, so concurrent callers await one request rather than racing. */
let inFlight = null

/**
 * The safety ceilings in force, or null before the first successful load.
 *
 * Callers MUST handle null rather than substituting their own numbers. A
 * component that invents a fallback limit reintroduces exactly the duplicate
 * this module removed — and it would be the copy nobody updates. Skip the
 * client-side warning instead and let the server answer.
 */
const safety = computed(() => profile.value?.protocol?.safety ?? null)

/** Dose grid in force (0.5 mg on the Quebec warfarin protocol), or null. */
const granularityMg = computed(() => profile.value?.protocol?.doseGranularityMg ?? null)

/** Human-readable name for display, e.g. "INESSS — Quebec". */
const protocolName = computed(() => profile.value?.protocol?.name ?? null)

/** The molecule the account is configured for. */
const molecule = computed(() => profile.value?.molecule ?? null)

/**
 * True when the account names a protocol this build does not recognise. The UI
 * must say so rather than showing limits as though a protocol were in force —
 * the server is meanwhile enforcing the strictest ceilings it has.
 */
const unresolved = computed(() => profile.value != null && profile.value.known === false)

/** True when the account has never chosen, and is running on the default. */
const usingDefault = computed(() => profile.value?.configured === false)

/**
 * True when this owner still has to confirm a protocol before using the app.
 *
 * NOT the same thing as `usingDefault`, and the difference is the whole reason
 * the server sends it. Every account that never chose is running on a default;
 * only an account CREATED since the question moved into Retroact is gated on
 * answering it. An account that has been working on its country's default since
 * last spring must never meet a blocking screen — it reaches the same choice
 * through Settings → Clinique, as it always has.
 *
 * The server decides (routes/clinical.routes.js), because the marker behind it
 * lives on the account row and an invited member must never be blocked by a
 * question only the owner can answer. Read here, never derived.
 */
const setupRequired = computed(() => profile.value?.setupRequired === true)

/**
 * Said once per session when the server answers without the field at all.
 *
 * Reading a missing field as `false` is the right default — a blocking dialog
 * must never be guessed into existence — but it also means a backend that
 * predates this feature disables it completely and says nothing. That is a very
 * expensive silence: the front end looks correct, the dialog simply never
 * appears, and there is nothing anywhere to explain why. One line in the
 * console turns "it does not work" into "that server is older than this build".
 */
let warnedAboutMissingField = false
function warnIfFieldMissing(payload) {
  if (warnedAboutMissingField) return
  if (!payload || typeof payload !== 'object') return
  if ('setupRequired' in payload) return
  warnedAboutMissingField = true
  console.warn(
    '[clinical] GET /clinical/profile answered without `setupRequired`, so the '
    + 'first-run protocol dialog is disabled. The backend serving this app is '
    + 'older than the front end — restart or redeploy it.'
  )
}

/**
 * What was actually written to the account, as opposed to what is being
 * enforced. Null means nobody has chosen: the server resolved a default.
 *
 * The distinction is the whole reason this is exposed. `protocol.id` is always
 * populated -- it is the resolved answer -- so comparing a selection against it
 * makes an unconfigured account look identical to a configured one, and the
 * Save button can never enable. Compare against this instead.
 */
const storedProtocolId = computed(() => profile.value?.storedProtocolId ?? null)

/**
 * The account's country and whether we enforce its guidance yet.
 *
 * `supported: false` matters clinically: the pharmacist is being checked
 * against another jurisdiction's numbers, and the screen has to say so.
 */
const jurisdiction = computed(() => profile.value?.jurisdiction ?? null)

/**
 * True when at least one enforced limit is this account's own rather than the
 * protocol's.
 *
 * Drives a banner, not a footnote. A screen that shows an adjusted ceiling
 * under a publisher's name is claiming that publisher wrote it.
 */
const customised = computed(() => profile.value?.customised === true)

/** Adjusted limits, each with the protocol value it replaced and the reason given. */
const overrides = computed(() => profile.value?.overrides ?? [])

/** Every field that MAY be adjusted, with the protocol's own value for each. */
const overridable = computed(() => profile.value?.overridable ?? [])

/** What the chosen protocol itself enforces, before this account's adjustments. */
const baseSafety = computed(() => profile.value?.protocol?.baseSafety ?? null)

/**
 * Stored adjustments this build could not parse.
 *
 * Non-empty means the account is being enforced against the protocol's value
 * for those fields — not against what somebody set. Never render this as a
 * normal state.
 */
const overridesDropped = computed(() => profile.value?.overridesDropped ?? [])

/**
 * True once both requests have landed. The settings screen renders nothing it
 * cannot source from the server, so this is what separates "still loading or
 * failed" from a screen with real limits on it.
 */
const loaded = computed(() => Boolean(catalogue.value && profile.value))

async function load({ force = false } = {}) {
  if (!force && profile.value && catalogue.value) return profile.value
  if (inFlight) return inFlight

  loading.value = true
  error.value = null
  errorStatus.value = null
  inFlight = (async () => {
    try {
      const [catalogueRes, profileRes] = await Promise.all([
        http.get('/clinical/catalogue'),
        http.get('/clinical/profile')
      ])
      catalogue.value = catalogueRes.data
      profile.value = profileRes.data
      warnIfFieldMissing(profile.value)
      return profile.value
    } catch (e) {
      errorStatus.value = e?.response?.status ?? null
      // `message` as well as `error`: the shared auth middleware answers with
      // `{ message }`, so reading only `{ error }` turned every 401 into the raw
      // axios string ("Request failed with status code 401") on screen.
      error.value = e?.response?.data?.error || e?.response?.data?.message || e.message
      // Leave `profile` null rather than filling in a guess: every consumer
      // treats null as "no client-side check", which degrades to server-only
      // validation instead of to a wrong limit.
      throw e
    } finally {
      loading.value = false
      inFlight = null
    }
  })()
  return inFlight
}

/**
 * Persist a new selection. Owner-only server-side; a 403 here is expected for
 * invited members and should be surfaced, not swallowed.
 */
async function save({ moleculeId, protocolId }) {
  loading.value = true
  error.value = null
  try {
    const { data } = await http.put('/clinical/profile', { moleculeId, protocolId })
    await load({ force: true })
    // Non-empty when the protocol changed and took the account's adjusted
    // limits with it. The caller has to tell the owner: they should learn it
    // here, not by noticing a dose stop being rejected next week.
    return { cleared: data?.overridesCleared ?? [] }
  } catch (e) {
    error.value = e?.response?.data?.error || e.message
    throw e
  } finally {
    loading.value = false
  }
}

/**
 * Persist the account's adjusted limits.
 *
 * REPLACE semantics, matching the endpoint: `values` is the complete set, and a
 * field left out returns to the protocol's own number. Owner-only server-side.
 *
 * @param {Object} values  `{ <field>: { value, reason } }`
 * @returns {Promise<{advisories: Array}>}
 */
async function saveOverrides(values) {
  loading.value = true
  error.value = null
  fieldErrors.value = {}
  advisories.value = []
  try {
    const { data } = await http.put('/clinical/overrides', {
      // The protocol these values were reasoned about. The server refuses the
      // write if the selection changed underneath this screen, rather than
      // applying limits to a basis nobody checked them against.
      protocolId: profile.value?.protocol?.id ?? null,
      overrides: values
    })
    advisories.value = data?.advisories ?? []
    await load({ force: true })
    return { advisories: advisories.value }
  } catch (e) {
    const body = e?.response?.data
    error.value = body?.error || e.message
    // Rebuild the per-field map so the form can put each message on its input.
    if (Array.isArray(body?.errors)) {
      fieldErrors.value = body.errors.reduce((acc, item) => {
        if (item.field) acc[item.field] = item.message
        return acc
      }, {})
    }
    throw e
  } finally {
    loading.value = false
  }
}

/** Return every limit to the chosen protocol's own value. */
async function clearOverrides() {
  loading.value = true
  error.value = null
  fieldErrors.value = {}
  advisories.value = []
  try {
    const { data } = await http.delete('/clinical/overrides')
    await load({ force: true })
    return { cleared: data?.cleared ?? [] }
  } catch (e) {
    error.value = e?.response?.data?.error || e.message
    throw e
  } finally {
    loading.value = false
  }
}

export function useClinicalProtocol() {
  return {
    catalogue,
    profile,
    loading,
    error,
    fieldErrors,
    errorStatus,
    loaded,
    advisories,
    safety,
    granularityMg,
    protocolName,
    molecule,
    unresolved,
    usingDefault,
    setupRequired,
    storedProtocolId,
    jurisdiction,
    customised,
    overrides,
    overridable,
    baseSafety,
    overridesDropped,
    load,
    save,
    saveOverrides,
    clearOverrides
  }
}
